'use client';

import { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectRoutesTempStops,
  addRoutesTempStop,
  removeRoutesTempStop,
  updateRoutesTempStop,
} from 'src/redux';
import type { EnrichedRouteStop, RouteStopAddress } from './types';
import {
  entityAddressToRouteStopAddress,
  formatEntityAddress,
  formatAddressParts,
} from './utils';
import type { EntityAddress } from './utils';

export interface ProviderLike {
  id: string | number;
  name: string;
  lat: number;
  lng: number;
  address?: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
}

/**
 * Entity details structure from useGetEntityDetailsByIdQuery
 */
export interface EntityDetails {
  id: number | string;
  name: string;
  specialty?: string;
  primary_address?: EntityAddress;
  secondary_addresses?: EntityAddress[];
}

export interface UseStopSelectionOptions {
  formatAddress?: (provider: ProviderLike) => string;
}

export interface UseStopSelectionReturn {
  isInRoute: (providerId: string | number) => boolean;
  createStopFromProvider: (provider: ProviderLike) => EnrichedRouteStop;
  createStopFromEntityDetails: (entityDetails: EntityDetails) => EnrichedRouteStop;
  handleProviderToggle: (provider: ProviderLike) => void;
  addProviderToRoute: (provider: ProviderLike) => void;
  addEntityToRoute: (entityDetails: EntityDetails) => void;
  removeProviderFromRoute: (providerId: string | number) => void;
  updateStopAddress: (stopId: string, address: RouteStopAddress) => void;
}

const defaultFormatAddress = (provider: ProviderLike): string => {
  if (provider.address) {
    return provider.address;
  }
  return [
    provider.address_line_1,
    provider.address_line_2,
    provider.city,
    provider.state,
    provider.zip_code,
  ]
    .filter(Boolean)
    .join(', ');
};

/**
 * Get all available addresses from entity details
 */
const getAvailableAddresses = (entityDetails: EntityDetails): RouteStopAddress[] => {
  const addresses: RouteStopAddress[] = [];

  if (entityDetails.primary_address) {
    addresses.push(
      entityAddressToRouteStopAddress(entityDetails.primary_address, true)
    );
  }

  if (entityDetails.secondary_addresses) {
    entityDetails.secondary_addresses.forEach((addr) => {
      addresses.push(entityAddressToRouteStopAddress(addr, false));
    });
  }

  return addresses;
};

const useStopSelection = (
  options: UseStopSelectionOptions = {}
): UseStopSelectionReturn => {
  const { formatAddress = defaultFormatAddress } = options;

  const dispatch = useDispatch();
  const tempStops = useSelector(selectRoutesTempStops);

  const providerIdsInRoute = useMemo(() => {
    return new Set(tempStops.map((stop) => String(stop.providerId)));
  }, [tempStops]);

  const isInRoute = useCallback(
    (providerId: string | number): boolean => {
      return providerIdsInRoute.has(String(providerId));
    },
    [providerIdsInRoute]
  );

  const createStopFromProvider = useCallback(
    (provider: ProviderLike): EnrichedRouteStop => {
      return {
        id: `stop-${provider.id}-${Date.now()}`,
        providerId: String(provider.id),
        name: provider.name,
        address: formatAddress(provider),
        lat: provider.lat,
        lng: provider.lng,
        position: tempStops.length,
      };
    },
    [formatAddress, tempStops.length]
  );

  /**
   * Create a stop from entity details (includes address_key)
   * Defaults to primary address
   */
  const createStopFromEntityDetails = useCallback(
    (entityDetails: EntityDetails): EnrichedRouteStop | null => {
      const providerId = String(entityDetails.id);
      const availableAddresses = getAvailableAddresses(entityDetails);
      const primaryAddress = entityDetails.primary_address;

      // Validate we have a primary address with valid coordinates
      if (!primaryAddress || !primaryAddress.lat || !primaryAddress.lng) {
        console.error(
          '[useStopSelection] Cannot create stop - missing primary address or coordinates',
          { providerId, primaryAddress }
        );
        return null;
      }

      return {
        id: `stop-${providerId}-${Date.now()}`,
        providerId,
        name: entityDetails.name,
        address: formatEntityAddress(primaryAddress),
        lat: primaryAddress.lat,
        lng: primaryAddress.lng,
        position: tempStops.length,
        addressKey: primaryAddress.address_key,
        availableAddresses,
        provider: {
          id: providerId,
          name: entityDetails.name,
          specialty: entityDetails.specialty,
        },
      };
    },
    [tempStops.length]
  );

  const addProviderToRoute = useCallback(
    (provider: ProviderLike) => {
      const stop = createStopFromProvider(provider);
      dispatch(addRoutesTempStop(stop));
    },
    [createStopFromProvider, dispatch]
  );

  /**
   * Add a provider to the route using entity details (includes address_key)
   */
  const addEntityToRoute = useCallback(
    (entityDetails: EntityDetails) => {
      const stop = createStopFromEntityDetails(entityDetails);
      dispatch(addRoutesTempStop(stop));
    },
    [createStopFromEntityDetails, dispatch]
  );

  const removeProviderFromRoute = useCallback(
    (providerId: string | number) => {
      const stop = tempStops.find((s) => String(s.providerId) === String(providerId));
      if (stop) {
        dispatch(removeRoutesTempStop(stop.id));
      }
    },
    [tempStops, dispatch]
  );

  /**
   * Update a stop's address (when user changes from primary to secondary or vice versa)
   */
  const updateStopAddress = useCallback(
    (stopId: string, address: RouteStopAddress) => {
      const formattedAddress = formatAddressParts({
        addressLine1: address.addressLine1,
        addressLine2: address.addressLine2,
        city: address.city,
        state: address.state,
        zipCode: address.zipCode,
      });

      dispatch(
        updateRoutesTempStop({
          id: stopId,
          updates: {
            addressKey: address.addressKey,
            address: formattedAddress,
            lat: address.lat,
            lng: address.lng,
          },
        })
      );
    },
    [dispatch]
  );

  const handleProviderToggle = useCallback(
    (provider: ProviderLike) => {
      if (isInRoute(provider.id)) {
        removeProviderFromRoute(provider.id);
      } else {
        addProviderToRoute(provider);
      }
    },
    [isInRoute, removeProviderFromRoute, addProviderToRoute]
  );

  return {
    isInRoute,
    createStopFromProvider,
    createStopFromEntityDetails,
    handleProviderToggle,
    addProviderToRoute,
    addEntityToRoute,
    removeProviderFromRoute,
    updateStopAddress,
  };
};

export default useStopSelection;
